import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { AUTH_API_TIMEOUT_MS, formatErr } from "../lib/api";

const AuthContext = createContext(null);
const USER_CACHE_KEY = "bk_session_user";

function readCachedUser() {
  try {
    const value = JSON.parse(sessionStorage.getItem(USER_CACHE_KEY) || "null");
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

function cacheUser(user) {
  if (user && user !== false) sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  else sessionStorage.removeItem(USER_CACHE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      cacheUser(data.user);
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if ([401, 403].includes(status)) {
        localStorage.removeItem("buzzr_token");
        cacheUser(null);
        setUser(false);
      } else {
        setUser(readCachedUser() || false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { timeout: AUTH_API_TIMEOUT_MS });
    if (data.token) localStorage.setItem("buzzr_token", data.token);
    setUser(data.user);
    cacheUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("buzzr_token", data.token);
    if (data.user) setUser(data.user);
    if (data.user) cacheUser(data.user);
    return data;
  };

  const verifyEmail = async (email, code) => {
    const { data } = await api.post("/auth/verify-email", { email, code });
    if (data.token) localStorage.setItem("buzzr_token", data.token);
    setUser(data.user);
    cacheUser(data.user);
    return data.user;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    if (data.token) localStorage.setItem("buzzr_token", data.token);
    if (data.user) setUser(data.user);
    if (data.user) cacheUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("buzzr_token");
    cacheUser(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout, refresh, formatErr }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
